<template>
  <div class="card-with-icon" :class="[colorClass, layoutClass]">
    <div class="icon-container">
      <img 
        :src="iconSrc" 
        :alt="`${icon} icon`" 
        class="icon"
        width="200"
        height="200"
      />
    </div>
    <div class="icon-text-content">
      <p v-html="formattedText"></p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  color: {
    type: String,
    default: 'blue',
    description: 'Colour can be blue or orange',
    validator: (value) => ['blue', 'orange'].includes(value)
  },
  icon: {
    type: String,
    required: true,
    description: 'Icon name without the prefix/suffix (e.g., "research" for "icon_research_bg-blue.png")'
  },
  text: {
    type: String,
    required: true,
    description: 'Text content with **bold** words that will be highlighted in accent color'
  },
  layout: {
    type: String,
    default: 'single',
    description: 'Layout can be single (default) or two-column',
    validator: (value) => ['single', 'two-column'].includes(value)
  }
})

const colorClass = computed(() => `theme-${props.color}`)
const layoutClass = computed(() => `layout-${props.layout}`)

const formattedText = computed(() => {
  // Replace text wrapped in ** with styled spans
  return props.text.replace(
    /\*\*(.*?)\*\*/g,
    (_, text) => `<span class="highlight">${text}</span>`
  )
})

const iconSrc = computed(() => {
  return `/assets/images/icon_${props.icon}_bg-${props.color}.svg`;
})
</script>

<style lang="scss">
@use '@/assets/scss/main.scss';

.card-with-icon {
  // Base styles
  color: white;
  border-radius: $border-radius;
  padding: $spacing-md;
  display: flex;
  flex-direction: column;
  align-items: left;
  text-align: left;
  transition: transform $transition-base, box-shadow $transition-base;


  // Icon container
  .icon-container {
    .icon {
      width: 100%;
      height: auto;
      aspect-ratio: 1;
      object-fit: contain;
    }
  }

  // Text content
  .icon-text-content {
    flex: 1;
    font-size: 1.25rem;
    line-height: 1.25;
    @include proxima-regular;

    .highlight {
      @include proxima-bold;
      position: relative;
      text-decoration: none;
      
      &::after {
        content: '';
        position: absolute;
        left: -2px;
        right: -2px;
        bottom: -2px;
        height: 0.5rem;
        background-size: 100% 100%;
        background-repeat: no-repeat;
        z-index: 1;
      }
    }
  }

  // Layout variations
  &.layout-single {
    .icon-container {
      margin-bottom: $spacing-md;
    }
  }

  &.layout-two-column {
    flex-direction: row;
    align-items: center;
    gap: $spacing-lg;

    
    .icon-container {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      
      .icon {
        width: 100%;
        max-width: 200px;
      }
    }

    // Large screens
    @include media-up(xl) {
      gap: $spacing-xl;
      
      .icon-container .icon {
        max-width: 250px;
      }
    }

    // Medium screens
    @include media-down(lg) {
      gap: $spacing-md;
      
      .icon-container .icon {
        max-width: 180px;
      }
    }

    // Small screens
    @include media-down(md) {
      flex-direction: row;
      gap: $spacing-md;
      

      .icon-container {
        margin-bottom: $spacing-md;
        max-width: 150px;
      }
    }

    // Extra small screens
    @include media-down(sm) {
      flex-direction: column;
      column-gap: 0px;
      .icon-container  {
        max-width: 120px;
      }
      .icon-text-content {
        text-align: center;
        text-wrap: balance;
        margin: 0 auto;
        max-width: 12em;
      }
    }
  }

  // Theme variations
  &.theme-blue {
    background-color: $blue-dark;
    @include proxima-bold;

    .icon-text-content  .highlight {
      color: $orange;
      
      &::after {
        background-image: url('@/assets/images/undersmear_orange.svg');
      }
    }
  }

  &.theme-orange {
    background-color: $orange;
    @include proxima-bold;

    .icon-text-content  .highlight {
      color: $blue-dark;
      
      &::after {
        background-image: url('@/assets/images/undersmear_blue.svg');
      }
    }
  }

  // Responsive behavior
  @include media-down(md) {
    flex-direction: column;
    gap: $spacing-md;

    .icon-container {
      margin-bottom: $spacing-md;
    }
  }
}
</style>